import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Upload, Loader2, CheckCircle2, Briefcase } from "lucide-react";
import { VACANCIES } from "@/data/site";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-3 focus-visible:ring-2 focus-visible:ring-turq outline-none text-navy h-auto";

export default function Careers() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", position: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.position) { toast.error("Please fill name, phone and position."); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name); fd.append("phone", form.phone);
      fd.append("email", form.email); fd.append("position", form.position);
      if (file) fd.append("cv", file);
      await api.post("/careers", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Application submitted! Our HR team will contact you.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <section id="careers" className="py-20 md:py-28" data-testid="careers-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-turq-dark">Careers</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mt-3 tracking-tight">Work With Us</h2>
          <p className="text-secondary mt-5 text-lg leading-relaxed">
            Join DOABA Diagnostics Laboratory and become part of a professional healthcare team committed to excellence and patient care.
          </p>
          <div className="mt-7 grid sm:grid-cols-2 gap-3">
            {VACANCIES.map((v) => {
              const Icon = Icons[v.icon] || Briefcase;
              return (
                <button key={v.title} onClick={() => { setForm((f) => ({ ...f, position: v.title })); document.getElementById("career-name")?.focus(); }}
                  className="flex items-center gap-3 bg-white rounded-2xl p-4 border border-slate-100 shadow-sm hover:border-turq hover:-translate-y-0.5 transition-all text-left" data-testid={`vacancy-${v.title}`}>
                  <span className="w-10 h-10 rounded-xl bg-turq/10 flex items-center justify-center text-turq-dark shrink-0"><Icon className="w-5 h-5" strokeWidth={1.6} /></span>
                  <span className="text-sm font-medium text-navy">{v.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="bg-white rounded-[2rem] p-7 md:p-9 border border-slate-100 shadow-[0_20px_60px_-25px_rgba(10,37,64,0.2)]">
          {done ? (
            <div className="text-center py-10" data-testid="career-success">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-xl font-heading text-navy">Application Received!</h3>
              <p className="text-secondary mt-2">Thank you for applying. Our HR team will review your application and contact you soon.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="career-form">
              <h3 className="font-heading text-xl font-semibold text-navy">Apply Now</h3>
              <div><Label className="text-navy text-sm mb-1.5 block">Full Name *</Label><Input id="career-name" className={inputCls} value={form.name} onChange={set("name")} data-testid="career-name-input" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-navy text-sm mb-1.5 block">Phone *</Label><Input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="career-phone-input" /></div>
                <div><Label className="text-navy text-sm mb-1.5 block">Email</Label><Input className={inputCls} type="email" value={form.email} onChange={set("email")} data-testid="career-email-input" /></div>
              </div>
              <div><Label className="text-navy text-sm mb-1.5 block">Position Applying For *</Label>
                <select className={inputCls} value={form.position} onChange={set("position")} data-testid="career-position-select">
                  <option value="">Select a position</option>
                  {VACANCIES.map((v) => <option key={v.title} value={v.title}>{v.title}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-navy text-sm mb-1.5 block">Upload CV (PDF / DOC)</Label>
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="career-cv-input" />
                <button type="button" onClick={() => fileRef.current?.click()} className="w-full flex items-center gap-3 bg-slate-50 border border-dashed border-slate-300 rounded-2xl px-5 py-4 text-secondary hover:border-turq transition-colors" data-testid="career-cv-btn">
                  <Upload className="w-5 h-5 text-turq-dark" />
                  <span className="text-sm truncate">{file ? file.name : "Choose a file (PDF, DOC, DOCX)"}</span>
                </button>
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-turq hover:bg-turq-dark text-white py-6 text-base" data-testid="career-submit-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : "Apply Now"}
              </Button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
